import { AnyAction, Reducer } from 'redux';
import { deepEquals } from 'app/infrastructure/js';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import { appendAction } from './utils/append-action';
import { applySelection } from './utils/apply-selection';
import { compareModel } from './utils/compare-model';
import { activeItemSelected, applySelectionOnActiveItem } from './utils/selected-value';
import {
  ACTION,
  ClearAll,
  ClearList, RequestListBranch, ReceiveListBranch, FailListBranch,
  AddListAction, ClearActionErrors,
  UpdateSubscriberGroup, UpdateActiveValue,
  UpdateVisibility, SelectRow,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges, CancelChanges,
  ClearSubscriberGroupValue
} from './action-types';
import { UserMembersState, Visibility } from './types-store';
import { appendRowSelection } from 'app/area/subscriber-groups/user-members/utils/append-row-selection';

export const INITIAL_STATE: UserMembersState = {
  requestedModel: undefined,
  listBranch: [],
  listBranchState: [],
  listActions: [],
  actionErrors: [],
  updating: false,
  subscriberGroup: undefined,
  activeValueError: undefined,
  visiblity: Visibility.all,
  lastSelectedRow: undefined,
  selectedRows: []
};

const reducers: { [indexer: string]: Reducer<UserMembersState> } = {

  [ACTION.CLEAR_ALL]:
    (state: UserMembersState, action: ClearAll): UserMembersState => {

      return {
        ...state,
        ...INITIAL_STATE
      };
    },

  [ACTION.CLEAR_SUBSCRIBER_GROUP_VALUE]:
    (state: UserMembersState, action: ClearSubscriberGroupValue): UserMembersState => {

      return {
        ...state,
        subscriberGroup: undefined
      };
    },

  [ACTION.CLEAR_LIST]:
    (state: UserMembersState, action: ClearList): UserMembersState => {

      return {
        ...state,
        requestedModel: action.model,
        listBranch: [],
        listBranchState: [],
        listActions: [],
        actionErrors: [],
        activeValueError: undefined,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.REQUEST_LIST_BRANCH]:
    (state: UserMembersState, action: RequestListBranch): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }

      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, open: true };

      return {
        ...state,
        listBranchState: [
          ...state.listBranchState.filter(branch => branch !== currentListBranchState),
          {
            ...currentListBranchState,
            fetching: true,
            open: action.id === undefined || currentListBranchState.open
          }
        ]
      };
    },

  [ACTION.RECEIVE_LIST_BRANCH]:
    (state: UserMembersState, action: ReceiveListBranch): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }

      const currentListBranch = state.listBranch
        .find(branch => branch.id === action.id);
      const currentListBranchItems = currentListBranch?.items || [];
      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, open: false };

      return {
        ...state,
        listBranchState: [
          ...state.listBranchState.filter(branch => branch !== currentListBranchState),
          {
            ...currentListBranchState,
            fetching: false
          }
        ],
        listBranch: [
          ...state.listBranch.filter(branch => branch !== currentListBranch),
          {
            id: action.id,
            numberOfResults: action.numberOfResults,
            items: action.clearList
              ? action.listBranchItems
              : [
                ...currentListBranchItems,
                ...action.listBranchItems
              ]
          }
        ]
      };
    },

  [ACTION.FAIL_LIST_BRANCH]:
    (state: UserMembersState, action: FailListBranch): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }
      const currentListBranch = state.listBranch
        .find(branch => branch.id === action.id) ||
        { id: action.id, numberOfResults: 0, items: [] };
      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, fetching: false, open: false };

      return currentListBranchState.fetching
        ? {
          ...state,
          listBranchState: [
            ...state.listBranchState.filter(branch => branch !== currentListBranchState),
            {
              ...currentListBranchState,
              fetching: false
            }
          ],
          listBranch: [
            ...state.listBranch.filter(branch => branch !== currentListBranch),
            currentListBranch
          ]
        }
        : state;
    },

  [ACTION.ADD_LIST_ACTION]:
    (state: UserMembersState, action: AddListAction): UserMembersState => {

      return {
        ...state,
        listActions: action.actions.reduce(appendAction,
          (state.listActions || [])
        )
      };
    },

  [ACTION.CLEAR_ACTION_ERRORS]:
    (state: UserMembersState, action: ClearActionErrors): UserMembersState => {

      return {
        ...state,
        listActions: action.fixListActions
          ? state.listActions?.filter(listAction => !state.actionErrors?.some(
            message => message.parameters?.userId === listAction.path[listAction.path.length - 1]
          ))
          : state.listActions,
        actionErrors: []
      };
    },

  [ACTION.UPDATE_SUBSCRIBER_GROUP]:
    (state: UserMembersState, action: UpdateSubscriberGroup): UserMembersState => {

      return {
        ...state,
        subscriberGroup: action.value
      };
    },

  [ACTION.UPDATE_ACTIVE_VALUE]:
    (state: UserMembersState, action: UpdateActiveValue): UserMembersState => {

      return {
        ...state,
        subscriberGroup: action.value,
        listActions: [],
        actionErrors: [],
        activeValueError: undefined,
        visiblity: Visibility.all,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.UPDATE_VISIBILITY]:
    (state: UserMembersState, action: UpdateVisibility): UserMembersState => {

      return {
        ...state,
        visiblity: action.visibility,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.SELECT_ROW]:
    (state: UserMembersState, action: SelectRow): UserMembersState => {

      return {
        ...state,
        lastSelectedRow: action.selection.range
          ? state.lastSelectedRow
          : !state.selectedRows.some(path => deepEquals(path, action.selection.path))
            ? action.selection.path[action.selection.path.length - 1]
            : undefined,
        selectedRows: appendRowSelection(
          state.selectedRows,
          state.lastSelectedRow,
          state.listBranch,
          state.listBranchState,
          action.selection
        )
      };
    },

  [ACTION.REQUEST_SAVE_CHANGES]:
    (state: UserMembersState, action: RequestSaveChanges): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.subscriberGroup?.value !== action.activeValue?.value) {
        return state;
      }

      return {
        ...state,
        updating: true,
        actionErrors: [],
        activeValueError: undefined
      };
    },

  [ACTION.RECEIVE_SAVE_CHANGES]:
    (state: UserMembersState, action: ReceiveSaveChanges): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.subscriberGroup?.value !== action.activeValue?.value) {
        return state;
      }

      const listBranch = applySelection(
        state.listBranch,
        state.listActions,
        activeItemSelected(action.activeValue),
        applySelectionOnActiveItem(action.activeValue)
      );

      return {
        ...state,
        listActions: [],
        updating: false,
        listBranch
      };
    },

  [ACTION.FAIL_SAVE_CHANGES]:
    (state: UserMembersState, action: FailSaveChanges): UserMembersState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.subscriberGroup?.value !== action.activeValue?.value) {
        return state;
      }

      return {
        ...state,
        updating: false,
        actionErrors: action.actionErrors,
        activeValueError: action.activeValueError
      };
    },

  [ACTION.CANCEL_CHANGES]:
    (state: UserMembersState, action: CancelChanges): UserMembersState => {

      return {
        ...state,
        listActions: [],
        actionErrors: [],
        activeValueError: undefined
      };
    }
};

const stateReducer: Reducer<UserMembersState> = (
  state: UserMembersState = INITIAL_STATE, action: AnyAction) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.subscriberGroups, stateReducer);
